// The Wizard panel: ask for chords, bass, a melody, an arp, a harmony line, a fill or a groove in
// today's key and sounds. Suggestions play along as ghost notes; accepting adds them — and every
// suggestion you receive counts towards your loop's Wizard mark, accepted or not.
import { useEffect, useState } from "react";
import { ApiError } from "../api";
import { Link } from "../lib/router";
import { useEditor, useView } from "./store";
import {
  acceptSuggestion,
  conjure,
  discardSuggestion,
  KIND_LABEL,
  mixDown,
  KIND_TARGET,
  refreshMark,
  useWizard,
  type WizardKind,
} from "./wizardClient";

const GENRES = ["", "pop", "house", "hiphop", "lofi", "trance", "jazz", "funk", "synthwave", "dnb", "afro"];
const MOODS = ["", "happy", "sad", "dreamy", "dark", "tense"];

export function WizardPanel() {
  const w = useWizard();
  const server = useEditor((s) => s.server);
  const readOnly = useEditor((s) => s.readOnly);
  const loop = useView();
  useEffect(() => {
    if (server) refreshMark();
  }, [loop, server]);
  if (readOnly) return null;
  // Signed out (a local draft): say where the Wizard is and how to get it.
  if (!server)
    return (
      <section className="wizard-panel panel" aria-label="Wizard" id="wizard" data-testid="wizard-teaser">
        <div className="row wizard-head">
          <h2>✨ Wizard</h2>
        </div>
        <p>
          The Wizard writes chords, bass, melodies, arpeggios, fills and grooves with you, in the day's key and sounds.{" "}
          <Link href={`/signin?next=${encodeURIComponent("/write")}`}>Sign in</Link> to use it.
        </p>
      </section>
    );
  const pct = w.markPreview !== null ? Math.round(w.markPreview * 100) : null;
  return (
    <section className="wizard-panel panel" aria-label="Wizard" id="wizard">
      <div className="row wizard-head">
        <h2>✨ Wizard</h2>
        <span className="muted small">
          Runs on the server · every suggestion counts towards your loop's Wizard mark
        </span>
      </div>
      <div className="row">
        <label className="field">
          <span>Idea</span>
          <select
            value={w.kind}
            onChange={(e) =>
              w.set({ kind: e.target.value as WizardKind, target: KIND_TARGET[e.target.value as WizardKind] })
            }
            data-testid="wizard-kind"
          >
            {(Object.keys(KIND_LABEL) as WizardKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        {w.kind === "fill" && (
          <label className="field">
            <span>On</span>
            <select value={w.target} onChange={(e) => w.set({ target: Number(e.target.value) })}>
              <option value={0}>Drums</option>
              <option value={16}>Bass</option>
              <option value={17}>Harmony</option>
              <option value={18}>Lead</option>
              <option value={19}>Classics</option>
            </select>
          </label>
        )}
        {(w.kind === "melody" || w.kind === "arp" || w.kind === "chords") && (
          <label className="field">
            <span>On</span>
            <select
              value={w.target}
              onChange={(e) => w.set({ target: Number(e.target.value) })}
              data-testid="wizard-target"
            >
              {w.kind === "melody" ? <option value={18}>Lead</option> : <option value={17}>Harmony</option>}
              <option value={19}>Classics</option>
            </select>
          </label>
        )}
        <label className="field">
          <span>Genre</span>
          <select value={w.genre} onChange={(e) => w.set({ genre: e.target.value })}>
            {GENRES.map((g) => (
              <option key={g} value={g}>
                {g || "today's style"}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Mood</span>
          <select value={w.mood} onChange={(e) => w.set({ mood: e.target.value })}>
            {MOODS.map((m) => (
              <option key={m} value={m}>
                {m || "from the key"}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Adventure</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={w.complexity}
            onChange={(e) => w.set({ complexity: Number(e.target.value) })}
          />
        </label>
        <label className="field">
          <span>Busy</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={w.density}
            onChange={(e) => w.set({ density: Number(e.target.value) })}
          />
        </label>
      </div>
      <div className="row">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void conjure(true)}
          disabled={w.busy}
          data-testid="wizard-conjure"
        >
          {w.busy ? "Conjuring…" : w.suggestion ? "Another idea" : "Conjure"}
        </button>
        {w.suggestion && (
          <>
            <button type="button" className="btn" onClick={acceptSuggestion} data-testid="wizard-accept">
              Accept
            </button>
            <button type="button" className="btn btn-ghost" onClick={discardSuggestion}>
              Discard
            </button>
            <span className="small" data-testid="wizard-desc">
              {w.suggestion.description} · {w.suggestion.events.length} notes (dashed in the grid, playing along)
            </span>
          </>
        )}
      </div>
      {w.error && (
        <p role="alert" className="error">
          {w.error}
        </p>
      )}
      <MixDown />
      {pct !== null && pct > 0 && (
        <p className="wizard-badge" data-testid="mark-preview">
          ✨ This loop will be marked: {pct} % Wizard
        </p>
      )}
    </section>
  );
}

/** The Wizard's mix-down: balance the whole song in one step (undo brings your mix back). */
function MixDown() {
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState<string[] | null>(null);
  const [error, setError] = useState("");
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      setChanges(await mixDown());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mixdown" data-testid="mixdown">
      <div className="row">
        <button type="button" className="btn" onClick={() => void run()} disabled={busy} data-testid="mixdown-run">
          {busy ? "Listening to your parts…" : "🎚 Mix it down"}
        </button>
        <span className="muted small">
          Balances drums, bass, chords, melody and atmosphere and places them on a stage — your effects stay. Undo
          brings your mix back.
        </span>
      </div>
      {changes && (
        <p className="small" data-testid="mixdown-changes">
          {changes.join(" · ")}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </div>
  );
}
